import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { services } from "@/data/services";
import "./Services.css";

const MARK =
  "svc-mark mt-6 w-[7.5rem] md:w-[8.25rem] shrink-0 rounded-md border border-slate-200 bg-slate-50 p-2 xl:mt-0 xl:w-[9.25rem] xl:absolute xl:top-10 xl:right-10";

function WebMark() {
  return (
    <div className={MARK} aria-hidden="true">
      <div className="flex items-center gap-1 rounded border border-slate-200 bg-white px-1.5 py-1">
        <span className="flex shrink-0 gap-0.5">
          <i className="block h-1 w-1 rounded-full bg-slate-300" />
          <i className="block h-1 w-1 rounded-full bg-slate-300" />
          <i className="block h-1 w-1 rounded-full bg-slate-300" />
        </span>
        <span className="h-1 min-w-0 flex-1 rounded-sm bg-slate-100" />
      </div>
      <div className="mt-1.5 flex gap-1">
        <span className="h-1 w-5 rounded-sm bg-slate-800" />
        <span className="h-1 w-3 rounded-sm bg-slate-200" />
        <span className="h-1 w-3 rounded-sm bg-slate-200" />
      </div>
      <div className="mt-1.5 rounded bg-white px-1.5 py-1 ring-1 ring-transparent transition-all motion-reduce:transition-none group-hover:ring-blue-200">
        <p className="text-[10px] font-semibold leading-none text-slate-800">Headline</p>
        <span className="mt-1 block h-1 rounded-sm bg-slate-100" />
        <span className="mt-1 block h-1 w-8 rounded-sm bg-slate-100" />
        <span className="mt-1.5 inline-flex rounded bg-blue-600 px-1.5 py-0.5 text-[8px] font-semibold leading-none text-white">View</span>
      </div>
      <div className="mt-1 grid grid-cols-2 gap-1">
        <div className="rounded bg-white px-1 py-1">
          <p className="text-[8px] font-semibold leading-none text-slate-700">Page</p>
          <span className="mt-1 block h-1 rounded-sm bg-slate-100" />
        </div>
        <div className="rounded bg-white px-1 py-1">
          <p className="text-[8px] font-semibold leading-none text-slate-700">Story</p>
          <span className="mt-1 block h-1 rounded-sm bg-slate-100" />
        </div>
      </div>
      <div className="mt-1 flex items-center justify-between rounded bg-white px-1.5 py-0.5">
        <span className="text-[8px] font-semibold leading-none text-slate-500">Menu</span>
        <span className="h-1 w-6 rounded-sm bg-slate-200" />
      </div>
    </div>
  );
}

function ShopMark() {
  return (
    <div className={MARK} aria-hidden="true">
      <div className="flex items-center justify-between rounded border border-slate-200 bg-white px-1.5 py-1">
        <span className="text-[9px] font-semibold leading-none text-slate-800">Store</span>
        <span className="h-2 w-2.5 rounded-[2px] border border-slate-300 bg-white" />
      </div>
      <div className="mt-1.5 rounded bg-white p-1 ring-1 ring-transparent transition-all motion-reduce:transition-none group-hover:ring-blue-200">
        <div className="h-12 rounded-sm bg-slate-100" />
        <p className="mt-1 text-[10px] font-semibold leading-none text-slate-800">Product</p>
        <div className="mt-1 flex items-center justify-between gap-1">
          <span className="text-[8px] leading-none text-slate-400">Price</span>
          <span className="rounded bg-blue-600 px-1 py-0.5 text-[8px] font-semibold leading-none text-white">Add</span>
        </div>
      </div>
      <div className="mt-1 grid grid-cols-2 gap-1">
        <div className="rounded bg-white px-1 py-1 text-[8px] font-semibold leading-none text-slate-600">Cart</div>
        <div className="rounded bg-white px-1 py-1 text-[8px] font-semibold leading-none text-blue-600 transition-colors motion-reduce:transition-none group-hover:bg-blue-50">
          Checkout
        </div>
      </div>
    </div>
  );
}

function MobileMark() {
  return (
    <div className={`${MARK} flex gap-1`} aria-hidden="true">
      <div className="flex min-w-0 flex-1 flex-col rounded-md border border-slate-200 bg-white p-1">
        <span className="mx-auto h-0.5 w-4 rounded-full bg-slate-200" />
        <p className="mt-1 text-[8px] font-semibold leading-none text-slate-800">Home</p>
        <div className="mt-1 space-y-1 transition-transform motion-reduce:transition-none motion-reduce:group-hover:translate-y-0 group-hover:-translate-y-px">
          <div className="rounded bg-slate-50 px-1 py-1">
            <p className="text-[8px] font-semibold leading-none text-slate-700">Inbox</p>
          </div>
          <div className="rounded bg-white px-1 py-1 ring-1 ring-slate-100">
            <p className="text-[8px] leading-none text-slate-500">Tasks</p>
          </div>
          <div className="rounded bg-white px-1 py-1 ring-1 ring-blue-100">
            <p className="text-[8px] leading-none text-slate-700">Update</p>
          </div>
          <div className="rounded bg-white px-1 py-1 ring-1 ring-slate-100">
            <p className="text-[8px] leading-none text-slate-500">Saved</p>
          </div>
        </div>
        <span className="mt-1 inline-flex w-fit rounded bg-blue-600 px-1 py-0.5 text-[8px] font-semibold leading-none text-white">Open</span>
        <span className="mt-1 flex justify-between">
          <i className="h-1 w-1 rounded-full bg-blue-500" />
          <i className="h-1 w-1 rounded-full bg-slate-200" />
          <i className="h-1 w-1 rounded-full bg-slate-200" />
        </span>
      </div>
      <div className="flex min-w-0 flex-1 flex-col rounded-md border border-slate-200 bg-white p-1">
        <span className="mx-auto h-0.5 w-4 rounded-full bg-slate-200" />
        <p className="mt-1 text-[8px] font-semibold leading-none text-slate-700">Detail</p>
        <span className="mt-1 block h-6 rounded-sm bg-slate-100" />
        <p className="mt-1 text-[8px] font-semibold leading-none text-slate-700">Screen</p>
        <p className="mt-1 text-[8px] leading-none text-slate-400">Notes</p>
        <div className="mt-1 rounded bg-white px-1 py-1 ring-1 ring-slate-100">
          <p className="text-[8px] leading-none text-slate-500">Action</p>
        </div>
        <div className="mt-1 rounded bg-slate-50 px-1 py-1">
          <p className="text-[8px] leading-none text-slate-500">Saved</p>
        </div>
        <span className="mt-1 text-[8px] font-semibold leading-none text-blue-600">Next</span>
      </div>
    </div>
  );
}

function UxMark() {
  return (
    <div className={MARK} aria-hidden="true">
      <div className="flex items-center justify-between">
        <span className="text-[8px] font-semibold uppercase tracking-[0.14em] text-slate-400">Canvas</span>
        <span className="flex items-end gap-0.5">
          <i className="block h-1.5 w-px bg-slate-300" />
          <i className="block h-2.5 w-px bg-blue-400" />
          <i className="block h-1 w-px bg-slate-300" />
        </span>
      </div>
      <div className="mt-1.5 rounded bg-white px-1.5 py-1 ring-1 ring-blue-200 transition-all motion-reduce:transition-none group-hover:ring-transparent">
        <p className="font-heading text-[13px] font-semibold leading-none tracking-tight text-slate-900">Aa</p>
        <p className="mt-1 text-[8px] leading-none text-slate-400">Type</p>
      </div>
      <div className="mt-1 flex items-center gap-1">
        <span className="rounded bg-blue-600 px-1.5 py-0.5 text-[8px] font-semibold leading-none text-white">Button</span>
        <span className="rounded bg-white px-1.5 py-0.5 text-[8px] font-semibold leading-none text-slate-700 ring-1 ring-slate-200 transition-all motion-reduce:transition-none group-hover:ring-blue-300">
          Card
        </span>
      </div>
      <div className="mt-1 rounded bg-white px-1.5 py-1">
        <p className="text-[8px] font-semibold leading-none text-slate-700">Space</p>
        <span className="mt-1 block h-1 rounded-sm bg-blue-100" />
      </div>
      <div className="mt-1 grid grid-cols-4 gap-1">
        <span className="h-3 rounded-sm bg-slate-900" />
        <span className="h-3 rounded-sm bg-blue-600" />
        <span className="h-3 rounded-sm bg-slate-200" />
        <span className="h-3 rounded-sm bg-blue-100 ring-1 ring-blue-200" />
      </div>
      <div className="mt-1 flex items-center gap-1 rounded border border-dashed border-slate-200 bg-white px-1 py-1">
        <span className="text-[8px] leading-none text-slate-400">Field</span>
        <span className="h-1 min-w-0 flex-1 rounded-sm bg-slate-100" />
      </div>
    </div>
  );
}

function AppMark() {
  return (
    <div className={MARK} aria-hidden="true">
      <div className="flex items-center justify-between rounded border border-slate-200 bg-white px-1.5 py-1">
        <span className="text-[9px] font-semibold leading-none text-slate-800">Workspace</span>
        <span className="flex items-center gap-1">
          <i className="block h-1.5 w-1.5 rounded-full bg-blue-500" />
          <span className="text-[8px] leading-none text-slate-400">Live</span>
        </span>
      </div>
      <div className="mt-1.5 flex gap-1">
        <div className="flex w-[1.15rem] shrink-0 flex-col gap-1 rounded bg-white p-1">
          <span className="block h-1 rounded-sm bg-blue-500" />
          <span className="block h-1 rounded-sm bg-slate-200" />
          <span className="block h-1 rounded-sm bg-slate-200" />
          <span className="block h-1 rounded-sm bg-slate-200" />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="rounded bg-white px-1 py-1 ring-1 ring-transparent transition-all motion-reduce:transition-none group-hover:ring-blue-200">
            <p className="text-[8px] font-semibold leading-none text-slate-800">Tasks</p>
            <span className="mt-1.5 block h-1 rounded-sm bg-slate-200" />
            <span className="mt-1 block h-1 w-7 rounded-sm bg-slate-100" />
            <span className="mt-1 block h-1 w-4 rounded-sm bg-blue-200" />
          </div>
          <div className="rounded bg-white px-1 py-1">
            <p className="text-[8px] font-semibold leading-none text-slate-700">Activity</p>
            <span className="mt-1.5 block h-1 rounded-sm bg-slate-100" />
            <span className="mt-1 block h-1 w-5 rounded-sm bg-slate-200" />
          </div>
          <div className="flex items-center justify-between rounded bg-white px-1 py-1">
            <span className="text-[8px] font-semibold leading-none text-slate-700">Open</span>
            <i className="block h-1.5 w-1.5 rounded-full bg-blue-500" />
          </div>
        </div>
      </div>
      <span className="mt-1 block h-1 rounded-sm bg-slate-200" />
    </div>
  );
}

const PREVIEWS = {
  "web-development": WebMark,
  "shopify-development": ShopMark,
  "mobile-development": MobileMark,
  "ui-ux-design": UxMark,
  "custom-applications": AppMark,
};

const workspace = [
  { id: "web", name: "Website", group: "Web experience", status: "Active", points: ["Website", "Pages", "Content", "Navigation"] },
  { id: "commerce", name: "Commerce", group: "Commerce", status: "Ready", points: ["Storefront", "Products", "Checkout", "Cart"] },
  { id: "mobile", name: "Mobile", group: "Mobile", status: "Open", points: ["Screens", "Navigation", "Tasks", "Messages"] },
  { id: "apps", name: "Applications", group: "Applications", status: "Live", points: ["Workflows", "Data", "Tools", "Portals"] },
  { id: "seo", name: "SEO", group: "Search", status: "Active", points: ["Pages", "Structure", "Content", "Visibility"] },
];

const projectChips = workspace.filter((item) => item.id === "web" || item.id === "commerce");
const connected = workspace.filter((item) => item.id !== "web");

function ServicesHero() {
  const [active, setActive] = useState("web");
  const current = workspace.find((item) => item.id === active) || workspace[0];

  return (
    <section className="services-hero border-b border-slate-200">
      <div className="services-hero-wrap">
        <div className="services-hero-copy">
          <p className="services-hero-kicker">Core services</p>
          <h1 className="services-hero-title font-heading">Digital Services Built Around How Your Business Works.</h1>
          <p className="services-hero-lead">
            From websites and commerce platforms to mobile products, custom applications, and search visibility, we design and build digital systems around the way your business actually operates.
          </p>
          <Link to="/contact" className="group services-hero-cta">
            Start a Project
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="services-hero-visual">
          <div className="services-hero-board">
            <div className="services-hero-bar">
              <span>Digital workspace</span>
              <span className="services-hero-live"><i />Live</span>
            </div>
            <div className="services-hero-project">
              <span>Project</span>
              <strong>BizLogic Digital System</strong>
              <div className="services-hero-chips">
                {projectChips.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={active === item.id ? "is-on" : ""}
                    aria-pressed={active === item.id}
                    onClick={() => setActive(item.id)}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="services-hero-stage">
              <div className="services-hero-panel" key={current.id}>
                <span>Active service</span>
                <strong>{current.group}</strong>
                <ul>
                  {current.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
                <em>View service</em>
              </div>
              <div className="services-hero-side">
                <span>Connected</span>
                <div className="services-hero-links">
                  {connected.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={active === item.id ? "is-on" : ""}
                      aria-pressed={active === item.id}
                      onClick={() => setActive(item.id)}
                    >
                      <b>{item.name}</b>
                      <small>{item.status}</small>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="services-hero-phone">
            <div className="services-hero-bar">
              <span>Digital workspace</span>
              <span className="services-hero-live"><i />Live</span>
            </div>
            {workspace.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`services-hero-line ${active === item.id ? "is-on" : ""}`}
                aria-pressed={active === item.id}
                onClick={() => setActive(item.id)}
              >
                <b>{item.name}</b>
                <small>{item.status}</small>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Services() {
  useEffect(() => {
    document.title = "Services | BizLogic Developers";
  }, []);

  return (
    <div data-testid="services-page" className="pt-20">
      <ServicesHero />

      <section>
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-8 lg:py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            {services.map((srv, idx) => {
              const Preview = PREVIEWS[srv.id];
              return (
                <Link
                  key={srv.id}
                  to={`/services/${srv.id}`}
                  id={srv.id}
                  data-testid={`service-section-${idx}`}
                  className="relative bg-white p-8 lg:p-10 flex flex-col items-start hover:bg-slate-50 transition-colors group xl:min-h-[280px]"
                >
                  <span className="font-mono text-sm text-slate-400 mb-4 xl:mb-8 xl:min-h-[9.375rem]">{String(idx + 1).padStart(2, "0")}</span>
                  <p className="text-xs uppercase tracking-[0.18em] font-semibold text-cyan-600 font-heading mb-3">
                    {srv.badge}
                  </p>
                  <h2 className="text-2xl font-bold text-slate-950 font-heading group-hover:text-blue-600 transition-colors">
                    {srv.title}
                  </h2>
                  <p className="mt-3 text-slate-600 leading-relaxed">
                    {srv.summary}
                  </p>
                  {Preview ? <Preview /> : null}
                  <span className="mt-auto pt-8 inline-flex items-center gap-2 text-sm font-semibold text-blue-600">
                    {srv.hubCta}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform motion-reduce:transition-none motion-reduce:group-hover:translate-x-0" />
                  </span>
                </Link>
              );
            })}
            <Link
              to="/services/websiteseo"
              id="websiteseo"
              data-testid="service-section-5"
              className="relative bg-white p-8 lg:p-10 flex flex-col items-start hover:bg-slate-50 transition-colors group xl:min-h-[280px]"
            >
              <span className="font-mono text-sm text-slate-400 mb-4 xl:mb-8 xl:min-h-[9.375rem]">06</span>
              <p className="text-xs uppercase tracking-[0.18em] font-semibold text-cyan-600 font-heading mb-3">
                Marketing
              </p>
              <h2 className="text-2xl font-bold text-slate-950 font-heading group-hover:text-blue-600 transition-colors">
                Website SEO
              </h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Search visibility built around your website, your content, and the people you want to reach.
              </p>
              <div className="svc-mark mt-6 w-[7.5rem] md:w-[8.25rem] shrink-0 rounded-md border border-slate-200 bg-slate-50 p-2 xl:mt-0 xl:w-[9.25rem] xl:absolute xl:top-10 xl:right-10" aria-hidden="true">
                  <div className="flex items-center gap-1.5 rounded border border-slate-200 bg-white px-1.5 py-1 text-[10px] text-slate-400 transition-colors motion-reduce:transition-none group-hover:border-blue-300">
                    <span className="h-1 w-1 rounded-full bg-slate-300 transition-colors motion-reduce:transition-none group-hover:bg-blue-500" />
                    Search
                  </div>
                  <div className="mt-1.5 grid gap-1">
                    <div className="rounded bg-white px-1.5 py-1 ring-1 ring-blue-200 transition-all motion-reduce:transition-none group-hover:ring-transparent">
                      <p className="text-[10px] font-semibold leading-none text-slate-800"><span className="mr-1 font-mono text-[9px] text-blue-600">01</span>Website</p>
                      <p className="mt-1 text-[9px] leading-none text-slate-400">Page title</p>
                    </div>
                    <div className="rounded bg-white px-1.5 py-1 ring-1 ring-transparent transition-all motion-reduce:transition-none group-hover:ring-blue-300">
                      <p className="text-[10px] font-semibold leading-none text-slate-700"><span className="mr-1 font-mono text-[9px] text-slate-400 group-hover:text-blue-600 transition-colors motion-reduce:transition-none">02</span>Service page</p>
                      <p className="mt-1 text-[9px] leading-none text-slate-400">Description</p>
                    </div>
                    <div className="rounded bg-white px-1.5 py-1">
                      <p className="text-[10px] font-semibold leading-none text-slate-700"><span className="mr-1 font-mono text-[9px] text-slate-400">03</span>Content</p>
                      <p className="mt-1 text-[9px] leading-none text-slate-400">Description</p>
                    </div>
                  </div>
              </div>
              <span className="mt-auto pt-8 inline-flex items-center gap-2 text-sm font-semibold text-blue-600">
                Explore Website SEO
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform motion-reduce:transition-none motion-reduce:group-hover:translate-x-0" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">
              Need Something More Specific?
            </h2>
            <p className="mt-4 max-w-2xl text-slate-600 leading-relaxed">
              Every business has different requirements. BizLogic can discuss custom requirements, integrations, applications, and digital solutions that do not fit a single service card.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/contact"
              data-testid="services-footer-cta"
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3.5 rounded-xl shadow-md shadow-blue-600/10"
            >
              Start a Project
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
